import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import Seo from "@/components/feature/Seo";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/site";
import type { Topic } from "@/types/content";

const allTopics = topics as Topic[];

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") ?? "all";
  const [keyword, setKeyword] = useState(searchParams.get("keyword") ?? "");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return allTopics.filter((topic) => {
      const matchCategory = category === "all" || topic.category === category;
      const matchKeyword =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.summary.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [category, keyword]);

  const hotRanking = useMemo(
    () => [...allTopics].sort((a, b) => b.heat - a.heat).slice(0, 6),
    [],
  );

  const activeCategory = categories.find((c) => c.id === category) ?? categories[0];

  const handleCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") {
      next.delete("category");
    } else {
      next.set("category", id);
    }
    setSearchParams(next);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = keyword.trim();
    if (value) {
      next.set("keyword", value);
    } else {
      next.delete("keyword");
    }
    setSearchParams(next);
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "爆料91热议话题列表",
      description:
        "汇集综艺现场、影视热议、音乐现场、内容创作者、幕后花絮与行业观察的热门话题。",
      url: "https://gaohaoshuzi.cn/topics",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://gaohaoshuzi.cn/" },
        { "@type": "ListItem", position: 2, name: "热议话题", item: "https://gaohaoshuzi.cn/topics" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "爆料91热议话题",
      itemListElement: allTopics.slice(0, 9).map((topic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: topic.title,
        url: `https://gaohaoshuzi.cn/topic/${topic.id}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="爆料91热议话题 · 评论区热门讨论与舆论反应汇总"
        description="爆料91热议话题列表，按综艺现场、影视热议、音乐现场等板块聚合网友讨论、评论区观点与舆论反应，支持关键词搜索，实时滚动更新。"
        keywords="爆料91热议话题,爆料91热门讨论,爆料91评论区,爆料91网友讨论,爆料91舆论反应"
        path="/topics"
        ogType="website"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 lg:px-12 pt-6 pb-10 md:pb-14 bg-background-100">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "热议话题" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
              <i className="ri-fire-line"></i>
              共 {allTopics.length} 个话题 · 持续更新
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-black text-foreground-950 font-heading leading-tight">
              热议话题列表
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              当前正在 <strong className="font-bold text-foreground-900">{activeCategory.name}</strong> 板块，
              {activeCategory.desc}。切换分类或搜索关键词，快速找到你想看的讨论。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
            <div>
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                  {categories.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleCategory(item.id)}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${
                        category === item.id
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      <i className={item.icon}></i>
                      {item.name}
                    </button>
                  ))}
                </div>

                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 px-4 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors">
                    <i className="ri-search-line text-foreground-500 text-lg"></i>
                    <input
                      type="text"
                      value={keyword}
                      onChange={(event) => setKeyword(event.target.value)}
                      placeholder="搜索话题标题、标签或关键词"
                      aria-label="搜索话题"
                      className="w-full bg-transparent py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                    />
                    {keyword && (
                      <button
                        type="button"
                        aria-label="清空搜索"
                        onClick={() => setKeyword("")}
                        className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700"
                      >
                        <i className="ri-close-circle-fill"></i>
                      </button>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-foreground-800 transition-colors"
                  >
                    搜索
                  </button>
                </form>
              </div>

              <p className="mt-6 text-sm text-foreground-500">
                找到 <strong className="font-bold text-primary-600">{filtered.length}</strong> 个相关话题
              </p>

              {filtered.length > 0 ? (
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  {filtered.map((topic) => (
                    <TopicCard key={topic.id} topic={topic} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-2xl border border-background-200 bg-background-100 px-6 py-14 text-center">
                  <span className="mx-auto w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-900">没有找到相关话题</h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    换个关键词试试，或者切换到「全部话题」看看当前的热议内容。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      handleCategory("all");
                    }}
                    className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                </div>
              )}
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-2xl bg-background-100 border border-background-200 p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="w-6 h-6 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-fire-fill text-sm"></i>
                  </span>
                  热度排行
                </h2>
                <div className="mt-3">
                  {hotRanking.map((topic, index) => (
                    <TopicCard key={topic.id} topic={topic} rank={index + 1} variant="compact" />
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-primary-950 text-background-100 p-5">
                <h2 className="text-base font-bold text-background-50">有新的爆料线索？</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-200/80">
                  把你看到的讨论或可验证的线索提交给我们，核实后会整理成新的热议话题。
                </p>
                <Link
                  to="/submit"
                  className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-accent-500 text-primary-950 text-sm font-black hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill"></i>
                  我要投稿
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}